import { ChangeEventHandler, VFC, useState } from "react";
import { DataList } from "src/components/data-list";
import { useNodeDefs } from "src/hooks/use-node-def.hook";
import { nodeDefs } from "src/nodes";
import { css } from "src/styles/theme";
import { NodeData, NodeDefinition, NodeType } from "src/types/node.types";

export interface NodeTypeSelectionProps {
  type?: NodeType;
  onChange: (type: NodeType) => void;
}

export const NodeTypeSelection: VFC<NodeTypeSelectionProps> = (props) => {
  const [searchText, setSearchText] = useState("");
  const nodeDefList = useNodeDefs(searchText);

  const onChangeHandler: ChangeEventHandler<HTMLInputElement> = (e) => {
    setSearchText(e.target.value);
  };

  const onSelect = (nodeDef: NodeDefinition<NodeType, NodeData>) => {
    props.onChange(nodeDef.type);
  };

  const currentNodeDef = props.type ? nodeDefs[props.type] : undefined;

  return (
    <DataList.Root
      items={nodeDefList}
      onSelect={onSelect}
      defaultHighlighted={currentNodeDef}
    >
      <div className={cn.wrapper()}>
        <DataList.Filter>
          <input
            className={cn.search()}
            value={searchText}
            onChange={onChangeHandler}
            placeholder="Search"
            aria-label="Search node types"
            autoFocus
          />
        </DataList.Filter>
        <div className={cn.body()}>
          {nodeDefList.length === 0 && (
            <p className={cn.emptyResults()}>No results found</p>
          )}
          <ul className={cn.list()}>
            {nodeDefList.map((nodeDef, idx) => (
              <li key={idx}>
                <DataList.Item
                  item={nodeDef}
                  text={nodeDef.displayName}
                  highlightedClassName="highlighted"
                >
                  <button
                    className={cn.nodeTypeBtn()}
                    onClick={() => props.onChange(nodeDef.type)}
                  >
                    <span className={cn.nodeName()}>{nodeDef.displayName}</span>

                    {nodeDef.description && (
                      <span className={cn.nodeDescription()}>
                        {nodeDef.description}
                      </span>
                    )}
                  </button>
                </DataList.Item>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </DataList.Root>
  );
};

const cn = {
  wrapper: css({
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  }),
  search: css({
    applyReset: "input",
    width: "auto",
    margin: "$3 $6",
    padding: "$3",
    borderRadius: "$2",
    fontSize: "$sm",
  }),
  body: css({
    padding: "$3 $6",
    overflow: "auto",
  }),
  emptyResults: css({
    color: "$neutralText",
    fontSize: "$sm",
    textAlign: "center",
  }),
  list: css({
    margin: 0,
    padding: 0,
    listStyle: "none",
  }),
  nodeTypeBtn: css({
    applyReset: "btn",
    width: "100%",
    display: "block",
    margin: "0",
    padding: "$3",
    borderRadius: "$2",
    border: 0,

    "&:hover": {
      backgroundColor: "$neutralBg",
    },

    "&.highlighted": {
      backgroundColor: "$neutralBgHover",
    },
  }),
  nodeName: css({
    display: "block",
    color: "$neutralTextContrast",
    fontSize: "$base",
  }),
  nodeDescription: css({
    display: "block",
    marginTop: "$2",
    color: "$neutralText",
    fontSize: "$sm",
    lineHeight: 1.4,
  }),
};
